'use client'
import React, { useLayoutEffect, useRef, useState } from 'react'
import Image from 'next/image'

import { PrismicNextImage } from '@prismicio/next'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs'
import '@/styles/vendors/reactTabs'
import { PrismicLink, PrismicRichText } from '@prismicio/react'
import { cn } from '@/utils/twMerge'

type DataObject = Record<string, any>
type ItemObject = Record<string, any>

type IndicatorStyle = {
  left: number
  width: number
}

const CompliancePlatformComponent = ({ data }: { data: DataObject }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const tabListContainerRef = useRef<HTMLDivElement>(null)
  const [indicator, setIndicator] = useState<IndicatorStyle>({ left: 0, width: 0 })
  const [indicatorReady, setIndicatorReady] = useState(false)
  const [indicatorAnimated, setIndicatorAnimated] = useState(false)

  useLayoutEffect(() => {
    const container = tabListContainerRef.current
    if (!container) return

    const updateIndicator = () => {
      const tabs = container.querySelectorAll<HTMLElement>('[role="tab"]')
      const activeTab = tabs[tabIndex]
      if (!activeTab) return

      const containerRect = container.getBoundingClientRect()
      const tabRect = activeTab.getBoundingClientRect()
      // Match previous ::after inset: left/right -4px
      setIndicator({
        left: tabRect.left - containerRect.left - 4,
        width: tabRect.width + 8,
      })
      setIndicatorReady(true)
    }

    updateIndicator()

    // Enable transitions only after the first paint so load doesn't slide from 0.
    const enableMotion = window.requestAnimationFrame(() => {
      setIndicatorAnimated(true)
    })

    const resizeObserver = new ResizeObserver(updateIndicator)
    resizeObserver.observe(container)
    window.addEventListener('resize', updateIndicator)

    return () => {
      window.cancelAnimationFrame(enableMotion)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateIndicator)
    }
  }, [tabIndex, data.items])

  return (
    <section className="relative z-[10] overflow-hidden bg-primary-ink-100 py-[80px] lg:py-[120px]">
      <div className="absolute right-0 top-0 -z-[2]">
        <Image
          src="/images/compliance-bg-top.svg"
          alt="compliance-bg-top"
          width={0}
          height={0}
          sizes="100vw"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>

      <div className="container">
        <div className="text-center [&>h2>em]:block [&>h2>em]:not-italic [&>h2>em]:text-white [&>h2>em]:lg:-mt-3 [&>h2]:mb-16 [&>h2]:text-primary-green-85">
          <PrismicRichText field={data.primary.title} />
        </div>

        <Tabs selectedIndex={tabIndex} onSelect={(index) => setTabIndex(index)}>
          <div ref={tabListContainerRef} className="relative mx-auto my-0 max-w-96">
            <TabList className="m-0 flex justify-center border-b-0">
              {data.items.map((item: ItemObject, index: number) => {
                const key = `i-${index}`
                return (
                  <Tab
                    key={key}
                    className="relative cursor-pointer border-0 bg-transparent px-4 py-2 text-white outline-none focus:outline-none aria-selected:border-0 aria-selected:bg-transparent aria-selected:text-white">
                    {item.tab}
                  </Tab>
                )
              })}
            </TabList>

            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute bottom-[-5px] h-[2px] bg-[#0fd082]',
                indicatorReady ? 'opacity-100' : 'opacity-0',
                indicatorAnimated &&
                  'motion-safe:transition-[left,width,opacity] motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.25,0.1,0.25,1)]',
              )}
              style={{ left: indicator.left, width: indicator.width }}
            />
          </div>

          {data.items.map((item: ItemObject, index: number) => {
            const key = `i-${index}`
            const isActive = tabIndex === index

            return (
              <TabPanel key={key}>
                {/* Remount active panel content so fadeIn restarts; display toggle alone will not. */}
                {isActive ? (
                  <div key={tabIndex} className="mt-20 grid grid-cols-5 gap-8 motion-safe:animate-fadeIn">
                    <div className="col-span-5 text-white lg:col-span-2">
                      <div className="[&>h3]:text-white [&>p]:my-8 [&>ul>li]:relative [&>ul>li]:pl-8 [&>ul>li]:leading-8 [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:top-1 [&>ul>li]:before:h-5 [&>ul>li]:before:w-5 [&>ul>li]:before:bg-[url('/images/check.svg')] [&>ul>li]:before:content-[''] [&_a]:underline [&_a]:underline-offset-2 [&_a]:transition-colors [&_a:hover]:text-primary-green-85">
                        <PrismicRichText field={item.title} />
                        <PrismicRichText field={item.description} />
                      </div>

                      <PrismicLink field={item.btn_link} className="btn btn-secondary btn-default btn-interaction mt-8">
                        {item.btn_label}
                        <Image src="/images/angle-right-black.svg" width={20} height={16} alt="" aria-hidden="true" />
                      </PrismicLink>
                    </div>
                    <div className="col-span-5 rounded-md bg-primary-white-10 lg:col-span-3">
                      <PrismicNextImage field={item.logo} layout="responsive" style={{ height: 'auto', width: '100%' }} fallbackAlt="" />
                    </div>
                  </div>
                ) : null}
              </TabPanel>
            )
          })}
        </Tabs>
      </div>

      <div className="absolute bottom-0 left-0 -z-[2]">
        <Image
          src="/images/compliance-bg-bottom.svg"
          alt="compliance-bg-bottom"
          width={0}
          height={0}
          sizes="100vw"
          style={{ width: '100%', height: 'auto' }}
        />
      </div>
    </section>
  )
}

export default CompliancePlatformComponent
